import { createAxiosInstance } from "../utils/axios-instance";
import { createMsalInstance } from "../utils/create-msal-instance";
import { msalConfig } from "../config/msalConfig";
import { USER_AUTH_TYPE } from "../enums/user-auth-type.enum";
import {
  OwnerProfile,
  OwnerProfileProgram,
  OwnerProfileUpdatePayload,
  OwnerProfileValidationErrors,
  PromoteOwnerPayload,
} from "../models/owner-profile.model";

/**
 * API service for the internal Owner Profile Maintenance page.
 *
 * IMPORTANT: `GET /ownerprofile/access/` is an AUTHORIZATION check that returns
 * a boolean. It is NOT an owner search endpoint. The backend determines the
 * answer from `dbo.OwnerUpdateAuthorizedUsers`; the frontend only consumes the
 * boolean and never inspects that table or maintains its own allow-list.
 */

const apiBaseUrl = process.env.REACT_APP_INVITE_API_URL || "";
const apiUrl = (path: string): string => `${apiBaseUrl}${path}`;

/**
 * TEMPORARY DEV BYPASS ⚠️
 * ------------------------------------------------------------------------
 * Set to `true` to skip the `GET /ownerprofile/access/` authorization check
 * and treat the current user as authorized. This exists ONLY to unblock local
 * development while the backend adds the developer to
 * `dbo.OwnerUpdateAuthorizedUsers`.
 *
 * MUST be `false` before committing / deploying. Do NOT ship this as `true`.
 * ------------------------------------------------------------------------
 */
const BYPASS_OWNER_PROFILE_ACCESS_CHECK = false;

/** Error wrapper that preserves the HTTP status + backend validation errors. */
export class OwnerProfileApiError extends Error {
  status?: number;
  /** Field-level validation errors, e.g. { OwnerEmail: ["required", ...] }. */
  validationErrors?: OwnerProfileValidationErrors;

  constructor(error: unknown, fallback: string) {
    super(resolveErrorMessage(error, fallback));
    this.name = "OwnerProfileApiError";
    this.status = (error as any)?.response?.status;
    this.validationErrors = (error as any)?.response?.data?.errors;
  }
}

/**
 * Resolve a user-friendly message, flattening the API's validation `errors`
 * dictionary ({ Field: string[] }) when present.
 */
const resolveErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as any)?.response?.data;
  const validationErrors = data?.errors as
    | OwnerProfileValidationErrors
    | undefined;

  if (validationErrors && typeof validationErrors === "object") {
    const flattened = Object.values(validationErrors)
      .flat()
      .filter(Boolean);
    if (flattened.length) return flattened.join(" ");
  }

  return data?.message || (error as any)?.message || fallback;
};

/**
 * Build an authenticated Axios client for the employee (Customer Service /
 * Business) B2C user flow.
 *
 * IMPORTANT (Azure AD B2C): the authority MUST include the sign-in policy
 * (e.g. `.../b2c_1a_signin_comarchemployee`). The default `msalConfig.auth.authority`
 * from the environment is policy-less, and any MSAL endpoint resolution against
 * it fails with `endpoints_resolution_error` ("openid-configuration ... resource
 * removed"), which in turn triggers an interactive redirect loop.
 *
 * This mirrors the established employee-authenticated API pattern used by the
 * `/invite` thunks and modals: set the policy-qualified authority, then create
 * the MSAL instance before building the Axios client.
 */
const buildEmployeeClient = async () => {
  msalConfig.auth.authority = `${process.env.REACT_APP_MSAL_AUTHORITY}/${USER_AUTH_TYPE.COMARCH_EMPLOYEE}`;
  const msalInstance = await createMsalInstance();
  return createAxiosInstance(msalInstance);
};

const unwrap = <T,>(responseData: any): T =>
  responseData && typeof responseData === "object" && "data" in responseData
    ? (responseData.data as T)
    : (responseData as T);

const asArray = (value: unknown): any[] => (Array.isArray(value) ? value : []);

const normalizeProgram = (program: any): OwnerProfileProgram => ({
  enrollmentId: program?.enrollmentId,
  programType: program?.programType,
  loyaltyId: program?.loyaltyId,
  enrolled: Boolean(program?.enrolled),
  comarchEnrolled: Boolean(program?.comarchEnrolled),
  ownerEmail: program?.ownerEmail,
});

const normalizeOwnerProfile = (
  payload: any,
  fallbackAccountId: string
): OwnerProfile => {
  const owner = unwrap<any>(payload) ?? {};
  return {
    ownerAccountId: String(
      owner.ownerAccountId ?? owner.ownerAccountID ?? fallbackAccountId
    ),
    ownerFirstName: owner.ownerFirstName ?? "",
    ownerLastName: owner.ownerLastName ?? "",
    ownerEmail: owner.ownerEmail ?? "",
    programs: asArray(owner.programs).map(normalizeProgram),
  };
};

export const ownerProfileService = {
  /**
   * Authorization check for the Owner Profile page.
   * Returns `true` when the authenticated employee is allowed to use the page.
   */
  async checkAccess(): Promise<boolean> {
    // TEMPORARY: skip the backend authorization check during local development.
    if (BYPASS_OWNER_PROFILE_ACCESS_CHECK) {
      return true;
    }
    try {
      const client = await buildEmployeeClient();
      const response = await client.get(apiUrl("/ownerprofile/access/"));
      const access = unwrap<any>(response.data);

      if (typeof access === "boolean") return access;
      if (typeof access?.hasAccess === "boolean") return access.hasAccess;
      if (typeof access?.isAuthorized === "boolean") return access.isAuthorized;
      if (typeof access?.authorized === "boolean") return access.authorized;

      // Unexpected shape: fail closed rather than assume access.
      return false;
    } catch (error) {
      throw new OwnerProfileApiError(
        error,
        "Unable to verify access to the Owner Profile page."
      );
    }
  },

  /** Retrieve owner details for a given owner account id. */
  async getOwnerProfile(ownerAccountId: string): Promise<OwnerProfile> {
    try {
      const client = await buildEmployeeClient();
      const response = await client.get(
        apiUrl(`/ownerprofile/${encodeURIComponent(ownerAccountId)}`)
      );
      return normalizeOwnerProfile(response.data, ownerAccountId);
    } catch (error) {
      throw new OwnerProfileApiError(
        error,
        "Unable to find an owner for that owner account id."
      );
    }
  },

  /** Update supported owner profile fields. */
  async updateOwnerProfile(
    ownerAccountId: string,
    update: OwnerProfileUpdatePayload
  ): Promise<void> {
    try {
      const client = await buildEmployeeClient();
      await client.patch(
        apiUrl(`/ownerprofile/${encodeURIComponent(ownerAccountId)}/profile`),
        update
      );
    } catch (error) {
      throw new OwnerProfileApiError(
        error,
        "Unable to update the owner profile."
      );
    }
  },

  /** Promote a staff user to owner. */
  async promoteOwner(payload: PromoteOwnerPayload): Promise<void> {
    try {
      const client = await buildEmployeeClient();
      await client.post(apiUrl("/ownerprofile/promoteowner"), payload);
    } catch (error) {
      throw new OwnerProfileApiError(
        error,
        "Unable to promote the staff user to owner."
      );
    }
  },
};
